import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { List, X, Crosshair } from "@phosphor-icons/react";
import { BUSINESS } from "@/lib/siteData";

const LINKS = [
  { label: "About", id: "about" },
  { label: "Services", id: "services" },
  { label: "Gallery", id: "gallery" },
  { label: "Heritage", id: "heritage" },
  { label: "Reviews", id: "reviews" },
  { label: "Visit", id: "contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-300 ${
        scrolled ? "bg-coal/95 border-steel" : "bg-transparent border-transparent"
      }`}
    >
      <nav className="mx-auto max-w-[1400px] px-5 md:px-10 h-[68px] flex items-center justify-between">
        <button
          onClick={() => go("hero")}
          data-testid="nav-logo"
          className="flex items-center gap-2 group"
        >
          <Crosshair size={28} weight="bold" className="text-blood group-hover:rotate-90 transition-transform duration-500 drop-shadow-[0_0_8px_rgba(224,30,43,0.6)]" />
          <span className="font-display text-2xl md:text-3xl tracking-tight uppercase leading-none text-bone">
            N.P. <span className="text-blood drop-shadow-[0_0_10px_rgba(224,30,43,0.55)]">Guns</span>
          </span>
        </button>

        <div className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="font-mono text-xs uppercase tracking-[0.2em] text-bone/70 hover:text-brass transition-colors duration-200"
            >
              {l.label}
            </button>
          ))}
          <a
            href={BUSINESS.phoneHref}
            data-testid="nav-call-btn"
            className="font-mono text-xs uppercase tracking-[0.2em] bg-oxblood text-bone px-5 py-3 hover:bg-brass hover:text-ink transition-colors duration-200"
          >
            {BUSINESS.phone}
          </a>
        </div>

        <button
          className="md:hidden text-bone"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <List size={26} />}
        </button>
      </nav>

      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="md:hidden bg-coal border-t border-steel px-5 py-6 flex flex-col gap-5"
          data-testid="nav-mobile-menu"
        >
          {LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              className="text-left font-mono text-sm uppercase tracking-[0.2em] text-bone/80"
            >
              {l.label}
            </button>
          ))}
          <a href={BUSINESS.phoneHref} className="font-mono text-sm uppercase tracking-[0.2em] text-brass">
            {BUSINESS.phone}
          </a>
        </motion.div>
      )}
    </motion.header>
  );
}
